/** The map stage: a circuit, a dot, and the numbers that sit over them.
 *
 * The dot is the only thing on this page that moves at the screen's own rate.
 * It is moved by writing a `transform` onto one `<g>` from a
 * `requestAnimationFrame` loop rather than by re-rendering the component,
 * because the alternative — React state at sixty hertz — would rebuild the
 * whole stage, labels and overlay included, sixty times a second to change
 * two numbers. The position it writes comes from the same interpolation the
 * readouts use, so the dot and the timing strip are always describing the
 * same instant.
 *
 * What the bed is drawn along is decided in `projection.ts`, and the fit is
 * held still on purpose. With published geometry it is computed once, so the
 * circuit does not creep about as the car reaches a further corner; with no
 * geometry it is refitted from the car's own trail, but only when the trail
 * has actually grown, so the map settles rather than rescaling every frame —
 * and the map says so, because a circuit drawing itself as the car goes round
 * looks like a sketch of the real thing, and a spectator should know which
 * of the two they are looking at.
 * A circuit with no geometry and no trail yet gets the design's stylised loop
 * and no car at all — a dot placed on a decorative shape would be a claim
 * about where somebody is.
 *
 * The frame buffer is read during render. That is deliberate: this component
 * is already re-rendered by the ten-hertz clock above it, and copying a
 * hundred-odd points into state on every frame to satisfy a rule about
 * purity would cost more than it bought.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { PLACEHOLDER_PATH } from '@/components/TrackOutline';
import type { TrackGeometry } from '@/lib/types';
import { speedIn, speedLabel, speedWords } from '@/lib/units';
import { useSpeedUnit } from '@/lib/useSpeedUnit';

import {
  STAGE_HEIGHT,
  STAGE_WIDTH,
  bedRunsOf,
  boundsOf,
  finishOf,
  geometryBounds,
  pathOf,
  pathOfRuns,
  project,
  projectionFor,
  spanOf,
  trailIsDrawable,
} from './projection';
import type { Bounds, Projection, World } from './projection';
import { DELAY_MS, interpolateAt, renderClock } from './interpolate';
import type { CarState, SectorMark, Spot } from './interpolate';
import type { SampleRef } from './useLiveClock';

interface Fit {
  bounds: Bounds;
  projection: Projection;
}

/** How much a trail must outgrow the current fit before the map is redrawn
 *  to suit it. Three per cent is below the threshold of noticing. */
const REFIT_GROWTH = 1.03;

export interface LiveMapProps {
  /** The published outline: `undefined` while it is still being asked for,
   *  `null` once the answer is that there is none to draw. */
  geometry: TrackGeometry | null | undefined;
  samples: SampleRef;
  /** The readouts' view of the car, for the overlay and the stale look. */
  car: CarState | null;
  /** Observed sector crossings, drawn as S1 and S2. */
  marks: readonly SectorMark[];
  /** Where the lap rolled over, when no published geometry gives a line. */
  startLine: Spot | null;
  /** False while reconnecting or once the stream is over: the loop stops and
   *  the car is left where it was, dimmed. */
  running: boolean;
  trackName: string;
}

function metre(value: number): string {
  // U+2212, so a negative coordinate is as wide as a positive one in a
  // tabular column.
  return `${value < 0 ? '−' : ''}${Math.abs(value).toFixed(1)}`;
}

function gearLabel(gear: number): string {
  if (gear < 0) return 'R';
  if (gear === 0) return 'N';
  return String(gear);
}

export function LiveMap({
  geometry,
  samples,
  car,
  marks,
  startLine,
  running,
  trackName,
}: LiveMapProps) {
  const carRef = useRef<SVGGElement | null>(null);
  const [trailFit, setTrailFit] = useState<Fit | null>(null);
  const speedUnit = useSpeedUnit();

  const geometryFit = useMemo<Fit | null>(() => {
    const bounds = geometryBounds(geometry);
    return bounds ? { bounds, projection: projectionFor(bounds) } : null;
  }, [geometry]);

  // Without geometry the map is the car's own path, so the fit has to be
  // recomputed as that path grows — but slowly, and only when it grows.
  useEffect(() => {
    if (geometryFit) {
      setTrailFit(null);
      return;
    }
    const check = () => {
      const points: World[] = samples.current.map((sample) => [sample.frame.x, sample.frame.z]);
      const bounds = boundsOf(points);
      if (!bounds || !trailIsDrawable(points, bounds)) return;
      setTrailFit((previous) => {
        if (previous && spanOf(bounds) <= spanOf(previous.bounds) * REFIT_GROWTH) return previous;
        return { bounds, projection: projectionFor(bounds) };
      });
    };
    check();
    const timer = setInterval(check, 1000);
    return () => clearInterval(timer);
  }, [geometryFit, samples]);

  const fit = geometryFit ?? trailFit;

  const geometryBed = useMemo(() => {
    if (!geometryFit) return null;
    return pathOfRuns(geometryFit.projection, bedRunsOf(geometry)) || null;
  }, [geometryFit, geometry]);

  const trailBed =
    !geometryFit && trailFit
      ? pathOf(
          trailFit.projection,
          samples.current.map((sample) => [sample.frame.x, sample.frame.z] as World),
        )
      : '';

  const bed = geometryBed || trailBed;
  const finish = fit ? finishOf(fit.projection, geometry) : null;

  // The dot follows the interpolated position at the screen's rate. Nothing
  // in here touches React state.
  useEffect(() => {
    const projection = fit?.projection;
    const node = carRef.current;
    if (!projection || !node) return;
    if (!running) {
      node.style.opacity = '0.3';
      return;
    }

    let handle = requestAnimationFrame(function step() {
      const state = interpolateAt(samples.current, renderClock(Date.now(), DELAY_MS));
      const element = carRef.current;
      if (element) {
        if (state) {
          const [x, y] = project(projection, state.x, state.z);
          element.setAttribute('transform', `translate(${x.toFixed(2)} ${y.toFixed(2)})`);
          element.style.opacity = state.stalled ? '0.3' : '1';
          element.style.visibility = 'visible';
        } else {
          element.style.visibility = 'hidden';
        }
      }
      handle = requestAnimationFrame(step);
    });

    return () => cancelAnimationFrame(handle);
  }, [fit, running, samples]);

  const label = bed
    ? `${trackName || 'the circuit'}, with the car's position`
    : `${trackName || 'the circuit'} — no outline published yet`;

  return (
    <div
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: '20px 24px',
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <svg
        viewBox={`0 0 ${STAGE_WIDTH} ${STAGE_HEIGHT}`}
        role="img"
        aria-label={label}
        style={{ width: '100%', maxWidth: 760, overflow: 'visible' }}
      >
        {bed ? (
          <>
            <path
              d={bed}
              fill="none"
              stroke="var(--color-neutral-800)"
              strokeWidth={24}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <path d={bed} fill="none" stroke="var(--color-neutral-700)" strokeWidth={1} />
          </>
        ) : (
          <path
            d={PLACEHOLDER_PATH}
            fill="none"
            stroke="var(--color-neutral-800)"
            strokeWidth={24}
            strokeLinejoin="round"
            opacity={0.5}
          />
        )}

        {finish ? (
          <line
            x1={finish[0][0]}
            y1={finish[0][1]}
            x2={finish[1][0]}
            y2={finish[1][1]}
            stroke="var(--color-neutral-300)"
            strokeWidth={2.5}
          />
        ) : null}

        {!finish && startLine && fit
          ? (() => {
              const [x, y] = project(fit.projection, startLine.x, startLine.z);
              return (
                <g>
                  <circle cx={x} cy={y} r={3.5} fill="var(--color-neutral-300)" />
                  <text
                    x={x}
                    y={y - 8}
                    fill="var(--color-neutral-400)"
                    fontSize={9}
                    textAnchor="middle"
                    fontFamily="var(--font-body)"
                  >
                    S/F
                  </text>
                </g>
              );
            })()
          : null}

        {fit
          ? marks.map((mark) => {
              const [x, y] = project(fit.projection, mark.x, mark.z);
              return (
                <g key={`${mark.sector}-${mark.x}-${mark.z}`}>
                  <circle cx={x} cy={y} r={3} fill="var(--color-neutral-600)" />
                  <text
                    x={x + 8}
                    y={y + 3}
                    fill="var(--color-neutral-600)"
                    fontSize={9}
                    fontFamily="var(--font-body)"
                  >
                    S{mark.sector}
                  </text>
                </g>
              );
            })
          : null}

        {fit ? (
          <g ref={carRef} style={{ visibility: 'hidden' }}>
            <circle r={12} fill="var(--color-accent)" opacity={0.18} />
            <circle
              r={5}
              fill="var(--color-accent-200)"
              style={{ filter: 'drop-shadow(0 0 6px var(--color-accent))' }}
            />
          </g>
        ) : null}
      </svg>

      <div
        style={{
          position: 'absolute',
          top: 16,
          left: 20,
          display: 'flex',
          gap: 16,
          fontSize: 11,
          color: 'var(--color-neutral-600)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {car ? (
          <>
            <span>
              x {metre(car.x)} · z {metre(car.z)}
            </span>
            <span style={car.stalled ? { color: 'var(--color-warn)' } : undefined}>
              {car.stalled ? 'no frames' : car.onTrack ? 'on track' : 'off track'}
            </span>
          </>
        ) : (
          <span>waiting for the first frame</span>
        )}
        {geometry !== undefined && !geometryFit ? (
          // Said plainly: the shape on screen is the car's path so far, not
          // a survey. It fills in as the lap goes round.
          <span style={{ color: 'var(--color-neutral-500)' }}>
            no outline published · drawing the car&rsquo;s path
          </span>
        ) : null}
      </div>

      <div style={{ position: 'absolute', top: 14, right: 20, textAlign: 'right' }}>
        <div
          style={{
            fontSize: 10,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--color-neutral-500)',
          }}
        >
          speed
        </div>
        <div style={{ fontSize: 34, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {car ? Math.round(speedIn(car.speed, speedUnit.unit)) : '—'}
          {/* The unit is the switch: one click, remembered by this browser.
              The wire stays in km/h. */}
          <button
            type="button"
            onClick={speedUnit.toggle}
            title={`show ${speedLabel(speedUnit.other)}`}
            aria-label={`speed in ${speedWords(speedUnit.unit)}; switch to ${speedWords(speedUnit.other)}`}
            style={{
              fontSize: 12,
              color: 'var(--color-neutral-500)',
              marginLeft: 4,
              background: 'none',
              border: 0,
              padding: 0,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {speedLabel(speedUnit.unit)}
          </button>
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--color-neutral-500)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          gear{' '}
          <span style={{ color: 'var(--color-text)', fontSize: 16 }}>
            {car ? gearLabel(car.gear) : '—'}
          </span>
        </div>
      </div>
    </div>
  );
}

export default LiveMap;
